import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconLightBulbStepCourses,
  iconTranslateStepCourses,
} from '../../assets';
import { useTheme } from '../../theme';

export interface MultilingualText {
  en: string;
  es: string;
}

type LangKey = 'en' | 'es';

export interface MultilingualFieldProps {
  label: string;
  required?: boolean;
  value: MultilingualText;
  onChange: (value: MultilingualText) => void;
  multiline?: boolean;
  placeholder?: string;
  validationError?: string;
  hint?: string;
  /** Optional id for the visible input (for aria-describedby etc.) */
  id?: string;
  /** Identificador de teste para o container do campo multilíngue */
  dataE2e?: string;
}

export function MultilingualField({
  label,
  required = false,
  value,
  onChange,
  multiline = false,
  placeholder,
  validationError,
  hint,
  id,
  dataE2e,
}: MultilingualFieldProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [lang, setLang] = useState<LangKey>('en');

  const update = useCallback(
    (langKey: LangKey, text: string) => {
      onChange({ ...value, [langKey]: text });
    },
    [value, onChange]
  );

  const labelRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
  };

  const requiredAsteriskStyle: React.CSSProperties = {
    color: theme.palette.error.main,
  };

  const translateIconStyle: React.CSSProperties = {
    width: 18,
    height: 18,
    objectFit: 'contain',
  };

  const segmentedControlWrapStyle: React.CSSProperties = {
    display: 'flex',
    width: '100%',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius.lg,
    marginBottom: theme.spacing.sm,
    padding: theme.spacing.xs,
    overflow: 'hidden',
    gap: 0,
  };

  const segmentStyle = (
    active: boolean,
    position: 'first' | 'last'
  ): React.CSSProperties => ({
    flex: 1,
    padding: `${theme.spacing.xs}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.semibold,
    color: active ? theme.palette.text.primary : theme.palette.text.secondary,
    backgroundColor: active
      ? theme.palette.background.paper
      : theme.palette.background.default,
    border: active
      ? `1px solid ${theme.palette.primary.main}`
      : '1px solid transparent',
    borderRadius: active
      ? theme.shape.borderRadius.lg
      : position === 'first'
        ? `${theme.shape.borderRadius.lg} 0 0 ${theme.shape.borderRadius.lg}`
        : `0 ${theme.shape.borderRadius.lg} ${theme.shape.borderRadius.lg} 0`,
    cursor: 'pointer',
    textAlign: 'center',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: `${theme.spacing.inputY} ${theme.spacing.inputX}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.input.background,
    border: validationError
      ? `1px solid ${theme.palette.error.main}`
      : `1px solid ${theme.palette.border.input}`,
    borderRadius: theme.shape.borderRadius.sm,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    minHeight: 100,
    resize: 'vertical',
    marginTop: theme.spacing.md,
    border: validationError
      ? `1px solid ${theme.palette.error.main}`
      : `1px solid ${theme.palette.input.background}`,
  };

  const hintRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginTop: theme.spacing.xxs,
  };

  const hintStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.text.secondary,
  };

  const errorStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.error.main,
    marginTop: theme.spacing.xs,
    marginBottom: 0,
  };

  const lightBulbIconStyle: React.CSSProperties = {
    width: 18,
    height: 18,
    objectFit: 'contain',
    flexShrink: 0,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: theme.spacing.md,
  };

  const displayHint = hint ?? t('questionBank.create.multilingual_hint');
  const inputId =
    id ?? `multilingual-field-${label.replace(/\s/g, '-').toLowerCase()}`;

  return (
    <div style={sectionStyle} data-e2e={dataE2e}>
      <div style={labelRowStyle}>
        <span style={labelStyle}>{label}</span>
        {required && <span style={requiredAsteriskStyle}>*</span>}
        <img
          src={iconTranslateStepCourses}
          alt=""
          style={translateIconStyle}
          aria-hidden
        />
      </div>
      <div style={segmentedControlWrapStyle}>
        <button
          type="button"
          style={segmentStyle(lang === 'en', 'first')}
          onClick={() => setLang('en')}
          aria-pressed={lang === 'en'}
        >
          EN
        </button>
        <button
          type="button"
          style={segmentStyle(lang === 'es', 'last')}
          onClick={() => setLang('es')}
          aria-pressed={lang === 'es'}
        >
          ES
        </button>
      </div>
      {multiline ? (
        <textarea
          id={inputId}
          value={value[lang]}
          onChange={e => update(lang, e.target.value)}
          placeholder={placeholder}
          style={textareaStyle}
          aria-required={required}
          aria-invalid={Boolean(validationError)}
        />
      ) : (
        <input
          id={inputId}
          type="text"
          value={value[lang]}
          onChange={e => update(lang, e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
          aria-required={required}
          aria-invalid={Boolean(validationError)}
        />
      )}
      {validationError && (
        <p style={errorStyle} role="alert">
          {validationError}
        </p>
      )}
      <div style={hintRowStyle}>
        <img
          src={iconLightBulbStepCourses}
          alt=""
          style={lightBulbIconStyle}
          aria-hidden
        />
        <span style={hintStyle}>{displayHint}</span>
      </div>
    </div>
  );
}
